 
import { Grid, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@HomaHealth/ui-component-library';
import {
  Button,
  Care,
  Chip,
  Compliance,
  DottedText,
  HighPriority,
  LowPriority,
  MediumPriority,
  Monetization,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import { formatDate } from './helper';
import RiskRegisterConstants from '../messages/RiskRegisterConstants';
import moment from 'moment';
import { getAvatarIcon } from './TaskBoardHelper';
import type { CSSObject } from 'tss-react';
import { truncateString } from './riskPatientDetailsHelper';

export const getMitigationChips = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // Jan = 0, Dec = 11

  const quarter = Math.floor(month / 3) + 1;

  const chips: string[] = ['Current Quarter'];

  for (let i = 0; i < 6; i++) {
    if (i < 4 - quarter) {
      // Remaining quarters in the same year
      const nextQuarter = quarter + i + 1;
      chips.push(`Q${nextQuarter} - ${year}`);
    } else {
      // Full years ahead
      const futureYear = year + (i - (4 - quarter) + 1);
      chips.push(`${futureYear}`);
    }
  }

  return chips.slice(0, 7); // Always return 7 chips
};

export const TruncatedText = ({
  label,
  width,
  styles,
}: {
  label: string;
  width: string;
  styles?: CSSObject;
}) => {
  const useStyles = makeStyles<{ width: string; styles?: CSSObject }>()(
    (_theme, { width, styles }) => ({
      ellipses: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        width,
        whiteSpace: 'nowrap',
        '&:hover': {
          overflow: 'visible',
          whiteSpace: 'normal',
          height: 'auto',
        },
        ...(styles ?? {}),
      },
    }),
  );
  const { classes } = useStyles({ width, styles });
  return (
    <Typography
      fontSize="14px"
      fontType="semiBold"
      lineHeight="24px"
      textColor={patientAccessTheme.palette.grey[900]}
      className={classes.ellipses}
    >
      {label}
    </Typography>
  );
};

export const getPlannedInfoMessages = (selectedChip: any) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;

  if (selectedChip === 'Current Quarter') {
    return {
      plannedInfoTitle: 'Mitigation Starts This Quarter',
      plannedInfoSub:
        'Your mitigation plan starts now. The risk status is In Progress, and actions can proceed.',
      plannedBackground: '#17A39C1A',
      plannedTextColor: patientAccessTheme.palette.success[600],
    };
  }

  // Check for "Qn - YYYY" format
  const quarterMatch = selectedChip?.match(/^Q(\d) - (\d{4})$/);
  if (quarterMatch) {
    const [, quarter, year] = quarterMatch;
    const chipQuarter = parseInt(quarter);
    const chipYear = parseInt(year);

    if (chipYear === currentYear && chipQuarter > currentQuarter) {
      return {
        plannedInfoTitle: 'Mitigation is planned for a future quarter',
        plannedInfoSub:
          'It will remain in ‘Planned’ status until then. Please provide a reason for this delay to maintain transparency',
        plannedBackground: '#E1A9481A',
        plannedTextColor: '#94691D',
      };
    }
  }

  // Check if it's just a plain year (e.g., "2026")
  const yearMatch = selectedChip?.match(/^(\d{4})$/);
  if (yearMatch) {
    const chipYear = parseInt(yearMatch[1]);

    if (chipYear > currentYear + 1) {
      return {
        plannedInfoTitle: 'You’ve selected a date significantly in the future.',
        plannedInfoSub:
          'Delaying mitigation exposes your organization to compliance and fiduciary risks. Please reconsider or give a compelling reason for this delay.',
        plannedBackground: '#D6545E1A',
        plannedTextColor: '#D6545E',
      };
    } else if (chipYear === currentYear + 1) {
      return {
        plannedInfoTitle: 'You’ve scheduled mitigation for a later year.',
        plannedInfoSub:
          'Delaying action increases risk exposure. Please justify the delay — early action helps reduce fiduciary risk.',
        plannedBackground: '#E1A9481A',
        plannedTextColor: '#94691D',
      };
    }
  }

  // Default fallback
  return {
    plannedInfoTitle: '',
    plannedInfoSub: '',
    plannedBackground: '',
    plannedTextColor: '',
  };
};

export const getStatusChip = (label: any) => {
  let textColor: any = '';
  let backgroundColor: any = '';
  switch (label) {
    case 'New':
    case 'Risk':
      textColor = patientAccessTheme.palette.AIQChips?.textColor1;
      backgroundColor = patientAccessTheme.palette.AIQChips?.background1;
      break;
    case 'Planned':
    case 'Suggested':
      textColor = '#A227DA';
      backgroundColor = '#A227DA1A';
      break;
    case 'In Progress':
      textColor = patientAccessTheme.palette.primary?.main;
      backgroundColor = '#96A3D91A';
      break;
    case 'Mitigated':
    case 'Completed':
      textColor = patientAccessTheme.palette.AIQChips?.textColor2;
      backgroundColor = patientAccessTheme.palette.AIQChips?.background2;
      break;
    case 'Accepted':
    case 'Acknowledge':
      textColor = patientAccessTheme.palette.AIQChips?.textColor3;
      backgroundColor = patientAccessTheme.palette.AIQChips?.background3;
      break;
    case 'Not Started':
    case 'Queued':
      textColor = patientAccessTheme.palette.status.error400;
      backgroundColor = '#fff2f3';
      break;
    case 'In Review':
      textColor = patientAccessTheme.palette.warning[400];
      backgroundColor = '#feffe6';
      break;
    case 'Done':
      textColor = patientAccessTheme.palette.success[600];
      backgroundColor = '#e6f9f6';
      break;
    default:
      textColor = patientAccessTheme.palette.AIQChips?.textColor3;
      backgroundColor = patientAccessTheme.palette.AIQChips?.background3;
      break;
  }
  return (
    <Chip
      height="27px"
      borderRadius="8px"
      label={
        <Typography
          fontSize="12px"
          fontType="medium"
          lineHeight="21.24px"
          textColor={textColor}
        >
          {label}
        </Typography>
      }
      backgroundColor={backgroundColor}
      border={`1px solid ${textColor}`}
    />
  );
};

export const getImpactIcon = (impact: any) => {
  let icon = <></>;
  const styles = { height: '20px', width: '24px' };
  switch (impact) {
    case 'Compliance':
      icon = <Compliance style={{ height: '17px', width: '24px' }} />;
      break;
    case 'Cost':
      icon = <Monetization style={styles} />;
      break;
    case 'Care':
      icon = <Care style={styles} />;
      break;
  }
  return (
    <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
      {icon} {impact}
    </div>
  );
};

export const getSeverityIcon = (severity: string) => {
  let icon: any = <></>;
  switch (severity) {
    case 'Low':
      icon = <LowPriority />;
      break;
    case 'Medium':
      icon = <MediumPriority />;
      break;
    case 'High':
      icon = <HighPriority />;
      break;
    default:
      icon = <LowPriority />;
      break;
  }
  return (
    <div
      style={{
        display: 'flex',
        gap: '3px',
        alignItems: 'center',
      }}
    >
      {icon}
      {severity}
    </div>
  );
};

export const getRiskIdChip = (
  label: string,
  isActivity?: boolean,
  textColor?: string,
  styles?: any,
) => {
  return (
    <Chip
      width="80px"
      label={
        <Typography
          fontSize="12px"
          fontType="medium"
          lineHeight="21.24px"
          textColor={textColor ?? (isActivity ? '#F2763D' : '#D6545E')}
        >
          {label}
        </Typography>
      }
      backgroundColor={patientAccessTheme.palette.status.white}
      border={`1px solid ${patientAccessTheme.palette.grey[300]}`}
      borderRadius="36px"
      styleProps={styles}
    />
  );
};

export const formatRiskTable = (
  risks: any,
  handleClick: any,
  handleAction: any,
  handleAcknowledgeAction: any,
) => {
  const messages = RiskRegisterConstants.MAIN_PAGE;
  return risks?.map((risk: any) => [
    risk?._id,
    getRiskIdChip(risk?.risk_id),
    <div
      onClick={() => {
        handleClick(risk);
      }}
    >
      <DottedText
        typographyStyles={{
          fontSize: '14px',
          lineHeight: '24px',
          fontWeight: 500,
          color: patientAccessTheme.palette.primary[500],
        }}
        text={risk?.title}
      />
    </div>,
    <Grid container spacing={1}>
      {risk?.impact?.map((imp: string, idx: number) => (
        <Grid key={idx}>{getImpactIcon(imp)}</Grid>
      ))}
    </Grid>,
    formatDate(risk?.date_identified, messages.DATE_FORMAT) || '',
    getSeverityIcon(risk?.severity_level),
    getStatusChip(risk?.risk_status),
    <Button
      label={risk?.risk_status === 'New' ? 'Take Action' : 'Start Execution'}
      variant="outlined"
      color="primary"
      height="33px"
      width="136px"
      borderRadius="4px"
      onClick={() => handleAction(risk)}
    />,
    <Button
      label={'Acknowledge'}
      variant="outlined"
      color="primary"
      height="33px"
      width="136px"
      borderRadius="4px"
      onClick={() => handleAcknowledgeAction(risk)}
    />,
  ]);
};
export const formatRiskTableExp = (
  risks: any,
  handleClick: any,
  // handleAction: any,
  // handleAcknowledgeAction: any,
) => {
  // const messages = RiskRegisterConstants.MAIN_PAGE;
  return risks?.map((risk: any) => [
    risk?._id,
    <div
      onClick={() => {
        handleClick(risk);
      }}
      style={{ maxWidth: '400px', overflow: 'hidden' }}
    >
      <Tooltip
        disableFocusListener
        placement="top-start"
        title={<Typography fontSize="12px">{risk?.title}</Typography>}
        arrow
      >
        <div>
          <DottedText
            typographyStyles={{
              fontSize: '14px',
              lineHeight: '24px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              color: patientAccessTheme.palette.primary[500],
            }}
            text={truncateString(risk?.title, 40)}
          />
        </div>
      </Tooltip>
    </div>,
    <Grid container spacing={1}>
      {risk?.impact?.map((imp: string, idx: number) => (
        <Grid key={idx}>{getImpactIcon(imp)}</Grid>
      ))}
    </Grid>,

    formatDate(risk?.date_identifed, 'D MMMM YYYY') || '',

    getStatusChip(risk?.risk_status),
  ]);
};
export const formatRiskTableInsights = (
  risks: any,
  handleClick: any,
  handleAction: any,
  handleAcknowledgeAction: any,
) => {
  return risks.map((risk: any) => [
    risk?._id,
    <div
      onClick={() => {
        handleClick(risk);
      }}
    >
      <DottedText
        typographyStyles={{
          fontSize: '14px',
          lineHeight: '24px',
          fontWeight: 500,
          color: patientAccessTheme.palette.primary[500],
        }}
        text={risk?.title}
      />
    </div>,

    getSeverityIcon(risk?.severity_level),

    <Button
      label={risk?.risk_status === 'New' ? 'Take Action' : 'Start Execution'}
      variant="outlined"
      color="primary"
      height="33px"
      width="136px"
      borderRadius="4px"
      onClick={() => handleAction(risk)}
    />,
    <Button
      label={'Acknowledge'}
      variant="outlined"
      color="primary"
      height="33px"
      width="136px"
      borderRadius="4px"
      onClick={() => handleAcknowledgeAction(risk)}
    />,
  ]);
};
export const formatSuggestedTableInsightsExp = (
  suggestedData: any,
  // handleClick: any,
  handleSuggestedAction: any,
) => {
  return suggestedData.map((risk: any) => [
    risk?._id,
    <div
      onClick={() => {
        // handleClick(suggestedData);
      }}
    >
      <DottedText
        typographyStyles={{
          fontSize: '14px',
          lineHeight: '24px',
          fontWeight: 500,
          color: patientAccessTheme.palette.primary[500],
        }}
        text={risk?.name}
      />
    </div>,
    <Button
      label="Assign"
      variant="outlined"
      color="primary"
      height="33px"
      width="136px"
      borderRadius="4px"
      onClick={() => handleSuggestedAction(risk)}
    />,
  ]);
};
export const isPlannedForCurrentQuarter = (plannedQuarter: string): boolean => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
  const currentQuarterLabel = `Q${currentQuarter} - ${currentYear}`;
  if (!plannedQuarter) {
    return false;
  }

  return (
    plannedQuarter === 'Current Quarter' ||
    plannedQuarter === currentQuarterLabel ||
    plannedQuarter === currentYear.toString()
  );
};

export const formatInProgressRisks = (data: any[], handleClick: any) => {
  const formattedRows: any[] = [];
  data.map((item: any) => {
    const activities = Array.isArray(item.activity) ? item.activity : [];
    activities.forEach((activity: any) => {
      formattedRows.push([
        item._id || 'NA',
        getRiskIdChip(item?.risk_id),
        <div onClick={() => handleClick(item)}>
          <DottedText
            typographyStyles={{
              fontSize: '14px',
              lineHeight: '24px',
              fontWeight: 500,
              color: patientAccessTheme.palette.primary[500],
            }}
            text={item?.title}
          />
        </div>,
        activity?.name || 'NA',
        <Grid container spacing={1}>
          {item?.impact?.map((imp: string, idx: number) => (
            <Grid key={idx}>{getImpactIcon(imp)}</Grid>
          ))}
        </Grid>,
        moment(activity?.start_date).isValid()
          ? moment.utc(activity?.start_date).format('MMMM D, YYYY')
          : 'NA',
        getSeverityIcon(item?.severity_level),
        getStatusChip(item?.risk_status),
      ]);
    });
  });
  return formattedRows;
};

export const formatPlannedRisks = (data: any[], handleAction: any) => {
  return data.map((item: any) => {
    const activities = Array.isArray(item.activity) ? item.activity : [];

    const activityName =
      activities.length > 0 && activities[0] !== 'NA'
        ? activities
            .map((a: any) => a?.name)
            .filter(Boolean)
            .join(', ')
        : 'NA';

    return [
      item._id || 'NA',
      getRiskIdChip(item?.risk_id),
      <div
        onClick={() => {
          handleAction(item);
        }}
      >
        <DottedText
          typographyStyles={{
            fontSize: '14px',
            lineHeight: '24px',
            fontWeight: 500,
            color: patientAccessTheme.palette.primary[500],
          }}
          text={item?.title}
        />
      </div>,
      item.selected_delay_reason || 'NA',
      activityName,
      item.planned_quarter || 'NA',
      getSeverityIcon(item?.severity_level),
      <Button
        label={'Start Execution'}
        variant="outlined"
        color="primary"
        height="33px"
        width="136px"
        borderRadius="4px"
        onClick={() => handleAction(item)}
      />,
    ];
  });
};

export const formatAcceptedRisks = (data: any[], handleClick: any) => {
  return data.map((item: any) => {
    return [
      item._id || 'NA',
      getRiskIdChip(item?.risk_id),
      <div
        onClick={() => {
          handleClick(item);
        }}
      >
        <DottedText
          typographyStyles={{
            fontSize: '14px',
            lineHeight: '24px',
            fontWeight: 500,
            color: patientAccessTheme.palette.primary[500],
          }}
          text={item?.title}
        />
      </div>,
      item.selected_acceptance_reason || 'NA',
      <Grid container spacing={1}>
        {item?.impact?.map((imp: string, idx: number) => (
          <Grid key={idx}>{getImpactIcon(imp)}</Grid>
        ))}
      </Grid>,
      getSeverityIcon(item?.severity_level),
    ];
  });
};

export const formatMitigatedRisks = async (data: any[], handleClick: any) => {
  return data.map((item: any) => {
    const activity = item.activity || {};

    return [
      item._id || 'NA',
      getRiskIdChip(item?.risk_id),
      <div
        onClick={() => {
          handleClick(item);
        }}
      >
        <DottedText
          typographyStyles={{
            fontSize: '14px',
            lineHeight: '24px',
            fontWeight: 500,
            color: patientAccessTheme.palette.primary[500],
          }}
          text={item?.title}
        />
      </div>,
      activity.activity_outcome || 'NA',
      <Grid container spacing={1}>
        {item?.impact?.map((imp: string, idx: number) => (
          <Grid key={idx}>{getImpactIcon(imp)}</Grid>
        ))}
      </Grid>,
      activity.start_date
        ? new Date(activity.start_date).toLocaleDateString()
        : 'NA',
      activity.updatedAt
        ? new Date(activity.updatedAt).toLocaleDateString()
        : 'NA',
    ];
  });
};

export const removeEmptyFields = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj
      .map((item: any) => removeEmptyFields(item))
      .filter((item) =>
        typeof item === 'object' ? Object.keys(item).length > 0 : !!item,
      );
  }

  if (typeof obj === 'object' && obj !== null) {
    const cleanedObj: any = {};
    Object.entries(obj).forEach(([key, value]) => {
      const cleanedValue = removeEmptyFields(value);
      const isValueEmpty =
        cleanedValue === '' ||
        cleanedValue === null ||
        (Array.isArray(cleanedValue) && cleanedValue.length === 0) ||
        (typeof cleanedValue === 'object' &&
          !(cleanedValue instanceof Date) &&
          Object.keys(cleanedValue).length === 0);

      if (!isValueEmpty) {
        cleanedObj[key] = cleanedValue;
      }
    });
    return cleanedObj;
  }

  return obj;
};

export const formatActivityTable = (data: any[], handleClick: any) => {
  return data.map((item: any) => {
    return [
      item?.id,
      getRiskIdChip(item?.activity_id, true),
      <div
        onClick={() => {
          handleClick(item);
        }}
      >
        <DottedText
          typographyStyles={{
            fontSize: '14px',
            lineHeight: '24px',
            fontWeight: 500,
            color: patientAccessTheme.palette.primary[500],
          }}
          text={item?.name}
        />
      </div>,
      getAvatarIcon(item?.tracker),
    ];
  });
};

export const formatActivityData = (
  activities: any[],
  handleActivityClick: any,
) => {
  return activities?.map((activity) => [
    activity?._id,
    getRiskIdChip(activity?.activity_id, true),
    <div
      onClick={() => {
        handleActivityClick(activity);
      }}
    >
      <DottedText
        typographyStyles={{
          fontSize: '14px',
          lineHeight: '24px',
          fontWeight: 500,
          color: patientAccessTheme.palette.primary[500],
        }}
        text={activity?.name}
      />
    </div>,
    // <Grid container spacing={1}>
    //   {activity.primary_category?.map((imp: any, idx: number) => (
    //     <Grid item key={idx}>
    //       {getRiskIdChip(imp?.risk_id)}
    //     </Grid>
    //   ))}
    // </Grid>,
    activity?.primary_category,
    <Grid container spacing={1}>
      {activity?.impact?.map((imp: string, idx: number) => (
        <Grid key={idx}>{getImpactIcon(imp)}</Grid>
      ))}
    </Grid>,
    activity?.activity_status && getStatusChip(activity.activity_status),
    activity?.owner?.join(', ') || '-',
    activity?.activity_status === 'Suggested'
      ? activity?.suggested_start_date
        ? moment.utc(activity?.suggested_start_date).format('MMMM D, YYYY')
        : '-'
      : activity?.start_date
      ? moment.utc(activity?.start_date).format('MMMM D, YYYY')
      : '-',
    activity?.tracker ? getAvatarIcon(activity?.tracker) : '-',
  ]);
};

export const formatActivityDataExp = (
  activities: any[],
  handleActivityClick: any,
) => {
  const data: any[] = [];

  activities.map((activity: any) => {
    const innerData = [];
    innerData.push(activity._id);
    innerData.push(
      <div
        onClick={() => {
          handleActivityClick(activity);
        }}
      >
        <Tooltip
          disableFocusListener
          placement="top-start"
          title={<Typography fontSize="12px">{activity.name}</Typography>}
          arrow
        >
          <div>
            <DottedText
              typographyStyles={{
                fontSize: '14px',
                lineHeight: '24px',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                color: patientAccessTheme.palette.primary[500],
              }}
              text={truncateString(activity?.name, 25)}
            />
          </div>
        </Tooltip>
      </div>,
    );
    innerData.push(
      <div>
        <Tooltip
          disableFocusListener
          placement="top-start"
          title={
            <Typography fontSize="12px">{activity.primary_category}</Typography>
          }
          arrow
        >
          <div>
            <Typography
              cursor="pointer"
              fontType="medium"
              style={{
                fontSize: '14px',
                whiteSpace: 'nowrap',
              }}
              textColor={`${patientAccessTheme.palette.text.mediumemphasis}`}
            >
              {truncateString(activity?.primary_category, 30)}
            </Typography>
          </div>
        </Tooltip>
      </div>,
    );
    innerData.push(
      activity?.activity_status && getStatusChip(activity?.activity_status),
    );
    innerData.push(
      activity?.owner?.length > 0 ? (
        <div>
          <Tooltip
            disableFocusListener
            placement="top-end"
            title={
              <Typography fontSize="14px">
                {activity.owner?.join(', ')}
              </Typography>
            }
            arrow
          >
            <div>
              <Typography
                fontType="medium"
                cursor="pointer"
                style={{
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                }}
                textColor={`${patientAccessTheme.palette.text.mediumemphasis}`}
              >
                {truncateString(activity?.owner?.join(', '), 20)}
              </Typography>
            </div>
          </Tooltip>
        </div>
      ) : (
        <div>
          <Typography
            fontType="medium"
            cursor="pointer"
            style={{
              fontSize: '14px',
              whiteSpace: 'nowrap',
            }}
            textColor={`${patientAccessTheme.palette.text.mediumemphasis}`}
          >
            -
          </Typography>
        </div>
      ),
    );
    data.push(innerData);
  });

  return data;
};

export const getReportName = (report: string) => {
  let reportName = '';
  switch (report) {
    case 'Gag Clause Compliance Check':
      reportName = 'gagClauseReport';
      break;
    case 'Compensation Transparency Check':
      reportName = 'compensationTransparencyReport';
      break;
    case 'Provision Favorability Check':
      reportName = 'provisionMatrixReport';
      break;
    case 'Gap Analysis Check Plan Document and ASA':
      reportName = 'gapAnalysisReport';
      break;
    case 'Inter-Contract Compliance Stop Loss and ASA':
      reportName = 'interContractReport';
      break;
    case 'Gap Analysis Check Plan Document and PBM':
      reportName = 'interContractReport';
      break;
    case 'ERISA Regulatory Compliance Check':
      reportName = 'regulatoryReport';
      break;
    case 'Gap Analysis Check Plan Document and Stop Loss Policy':
      reportName = 'Gap Analysis';
      break;
    case 'Commercial Evaluation Report':
      reportName = 'feeEvaluationReport';
      break;
    default:
      reportName = '';
      break;
  }
  return reportName;
};
